<script setup>
import { format, isValid, parse } from 'date-fns';
import { computed, ref, watch } from 'vue';
import { CsButton } from '../button';
import { CsIcon } from '../icon';
import { CsInput } from '../input';
import { CsListbox, CsListboxItem } from '../listbox';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../popover';
import { CsScrollShadow } from '../scroll-shadow';
import CsDatePickerPanel from './CsDatePickerPanel.vue';
import { defaultShortcuts } from './utils/dateRangeCalculators.js';

/**
 * @typedef {object} DatePickerAdvancedProps
 * @property {string|Date|null} [startDate=null] - The initial start date value
 * @property {string|Date|null} [endDate=null] - The initial end date value
 * @property {Array} [shortcuts=defaultShortcuts] - Array of shortcut groups with structure: [{ title: string, shortcuts: [{ label: string, key: string, date: function }] }]. Import individual groups like quickShortcuts, lastNDaysShortcuts for customization
 * @property {boolean} [isEnabledShortcuts=true] - Whether to display the shortcuts panel. Set to false to show only calendar
 *
 * @slot default - Slot props: { isOpen: boolean, open: function, close: function, dateDisplay: string }
 * @slot default.isOpen - Whether the popover is currently open
 * @slot default.open - Function to open the date picker popover
 * @slot default.close - Function to close the date picker popover
 * @slot default.dateDisplay - Intelligently formatted date string based on applied (props) values. Shows shortcut label if active, or formatted date range: "Aug 1–9, 2025" (same month), "Jul 1–Aug 9, 2025" (different month), "Dec 1, 2024–Jan 9, 2025" (different year). Note: This reflects the applied state, not temporary selection during picker interaction
 *
 * @event apply - Emitted when user applies date selection
 * @event apply.startDate - The selected start date
 * @event apply.endDate - The selected end date
 */

/**
 * @type {DatePickerAdvancedProps}
 */
const props = defineProps({
  startDate: {
    type: [String, Date],
    default: null,
  },
  endDate: {
    type: [String, Date],
    default: null,
  },
  shortcuts: {
    type: Array,
    default: () => defaultShortcuts,
  },
  isEnabledShortcuts: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['apply', 'date-display']);

// Date validation utilities
function isValidDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') return false;

  // Check if it matches yyyy-MM-dd format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;

  // Parse and validate the actual date
  const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
  return isValid(parsedDate);
}

function formatDateForDisplay(date) {
  if (!date) return '';

  // Handle string dates
  if (typeof date === 'string') {
    if (!isValidDateString(date)) return '';
    date = parse(date, 'yyyy-MM-dd', new Date());
  }

  // Handle Date objects
  if (!isValid(date)) return '';

  return format(date, 'MMM dd, yyyy');
}

function formatDateForEdit(date) {
  if (!date) return '';

  // Handle string dates
  if (typeof date === 'string') {
    if (!isValidDateString(date)) return '';
    date = parse(date, 'yyyy-MM-dd', new Date());
  }

  // Handle Date objects
  if (!isValid(date)) return '';

  return format(date, 'yyyy-MM-dd');
}

function parseInputDate(dateString) {
  if (!dateString || !isValidDateString(dateString)) return null;
  return parse(dateString, 'yyyy-MM-dd', new Date());
}

function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

// Component state
const isPanelOpen = ref(false);

const tempValue = ref({
  startDate: props.startDate,
  endDate: props.endDate,
});

// Watch for prop changes and update tempValue when props change externally
watch(
  () => ({ startDate: props.startDate, endDate: props.endDate }),
  newProps => {
    // Only update if the panel is closed to avoid interfering with user interaction
    if (!isPanelOpen.value) {
      tempValue.value.startDate = newProps.startDate;
      tempValue.value.endDate = newProps.endDate;
    }
  }
);

// Input focus states
const isStartDateFocused = ref(false);
const isEndDateFocused = ref(false);

// Input refs
const startDateInputRef = ref(null);
const endDateInputRef = ref(null);

// Temporary input values for editing
const tempStartDateInput = ref('');
const tempEndDateInput = ref('');

// Helper function to check if a shortcut is active for given dates
function isShortcutActiveForProps(shortcut, startDate, endDate) {
  if (!shortcut.date) return false;

  const shortcutRange = shortcut.date();

  // Both start and end dates must match for the shortcut to be active
  return (
    isSameDay(startDate, shortcutRange.startDate) &&
    isSameDay(endDate, shortcutRange.endDate)
  );
}

// Computed display values
const startDateDisplay = computed(() => {
  if (isStartDateFocused.value) {
    return tempStartDateInput.value;
  }
  return formatDateForDisplay(tempValue.value.startDate);
});

const endDateDisplay = computed(() => {
  if (isEndDateFocused.value) {
    return tempEndDateInput.value;
  }
  return formatDateForDisplay(tempValue.value.endDate);
});

const dateDisplay = computed(() => {
  const currentStartDate = props.startDate;
  const currentEndDate = props.endDate;

  // If no dates selected
  if (!currentStartDate || !currentEndDate) {
    return '';
  }

  // Check if any shortcut is active using props values (applied state)
  if (props.isEnabledShortcuts && props.shortcuts.length > 0) {
    const activeShortcut = props.shortcuts
      .flatMap(group => group.shortcuts)
      .find(shortcut =>
        isShortcutActiveForProps(shortcut, currentStartDate, currentEndDate)
      );

    if (activeShortcut) {
      return activeShortcut.label;
    }
  }

  // No shortcut matched, format dates intelligently
  const startDate = new Date(currentStartDate);
  const endDate = new Date(currentEndDate);
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endMonth = endDate.getMonth();

  // Same date
  if (isSameDay(startDate, endDate)) {
    return format(startDate, 'MMM d, yyyy');
  }

  // Same month and year: "Aug 1-9, 2025"
  if (startYear === endYear && startMonth === endMonth) {
    return `${format(startDate, 'MMM d')}–${format(endDate, 'd, yyyy')}`;
  }

  // Different month, same year: "Jul 1–Aug 9, 2025"
  if (startYear === endYear) {
    return `${format(startDate, 'MMM d')}–${format(endDate, 'MMM d, yyyy')}`;
  }

  // Different year: "Jul 1, 2024–Aug 9, 2025"
  return `${format(startDate, 'MMM d, yyyy')}–${format(
    endDate,
    'MMM d, yyyy'
  )}`;
});

// Shortcut active state checker for tempValue (panel state)
function isShortcutActive(shortcut) {
  if (!shortcut.date) return false;

  const shortcutRange = shortcut.date();
  const currentStartDate = tempValue.value.startDate;
  const currentEndDate = tempValue.value.endDate;

  // Both start and end dates must match for the shortcut to be active
  return (
    isSameDay(currentStartDate, shortcutRange.startDate) &&
    isSameDay(currentEndDate, shortcutRange.endDate)
  );
}

// Shortcut active state checker for props (applied state) - used for dateDisplay

function handleOpen() {
  tempValue.value.startDate = props.startDate;
  tempValue.value.endDate = props.endDate;

  isPanelOpen.value = true;
}

function handleClose() {
  isPanelOpen.value = false;
}

function handleCancel() {
  handleClose();

  tempValue.value.startDate = props.startDate;
  tempValue.value.endDate = props.endDate;
}

// Computed property to determine if Apply button should be disabled
const isApplyDisabled = computed(() => {
  // Disable if no start date selected
  if (!tempValue.value.startDate) {
    return true;
  }

  return false;
});

function handleApply() {
  console.log('tempValue:', tempValue.value);
  // Handle case where user only selected start date - set end date same as start date
  if (tempValue.value.startDate && !tempValue.value.endDate) {
    tempValue.value.endDate = tempValue.value.startDate;
  }

  emit('apply', tempValue.value);
  emit('date-display', dateDisplay.value);
  isPanelOpen.value = false;
}

function handleRangeSelect(range) {
  tempValue.value.startDate = range.startDate;
  tempValue.value.endDate = range.endDate;
}

function handleShortcutClick(shortcut) {
  if (shortcut.date) {
    const range = shortcut.date();
    handleRangeSelect(range);
  }
}

// Start date input handlers
function handleStartDateFocus() {
  isStartDateFocused.value = true;
  tempStartDateInput.value = formatDateForEdit(tempValue.value.startDate);
}

function handleStartDateBlur() {
  isStartDateFocused.value = false;

  const inputValue = tempStartDateInput.value.trim();

  // Handle empty input
  if (!inputValue) {
    tempValue.value.startDate = null;
    return;
  }

  // Validate and update if valid
  if (isValidDateString(inputValue)) {
    const parsedDate = parseInputDate(inputValue);
    if (parsedDate) {
      // Smart range selection: if we have an end date and start date is after end date,
      // set end date to start date
      const currentEndDate = tempValue.value.endDate;
      let newEndDate = currentEndDate;

      if (currentEndDate && parsedDate > currentEndDate) {
        newEndDate = parsedDate;
      }

      handleRangeSelect({
        startDate: parsedDate,
        endDate: newEndDate,
      });
    }
  } else {
    // Invalid date, revert to previous value
    tempStartDateInput.value = formatDateForEdit(tempValue.value.startDate);
  }
}

function handleStartDateInput(value) {
  tempStartDateInput.value = value;

  // Real-time validation for smart range selection
  if (isValidDateString(value)) {
    const parsedDate = parseInputDate(value);
    if (parsedDate) {
      const currentEndDate = tempValue.value.endDate;
      let newEndDate = currentEndDate;

      // Smart end date adjustment
      if (currentEndDate && parsedDate > currentEndDate) {
        newEndDate = parsedDate;
      }

      handleRangeSelect({
        startDate: parsedDate,
        endDate: newEndDate,
      });
    }
  }
}

// End date input handlers
function handleEndDateFocus() {
  isEndDateFocused.value = true;
  tempEndDateInput.value = formatDateForEdit(tempValue.value.endDate);
}

function handleEndDateBlur() {
  isEndDateFocused.value = false;

  const inputValue = tempEndDateInput.value.trim();

  // Handle empty input
  if (!inputValue) {
    tempValue.value.endDate = null;
    return;
  }

  // Validate and update if valid
  if (isValidDateString(inputValue)) {
    const parsedDate = parseInputDate(inputValue);
    if (parsedDate) {
      // Smart range selection: if we have a start date and end date is before start date,
      // set start date to end date
      const currentStartDate = tempValue.value.startDate;
      let newStartDate = currentStartDate;

      if (currentStartDate && parsedDate < currentStartDate) {
        newStartDate = parsedDate;
      }

      handleRangeSelect({
        startDate: newStartDate,
        endDate: parsedDate,
      });
    }
  } else {
    // Invalid date, revert to previous value
    tempEndDateInput.value = formatDateForEdit(tempValue.value.endDate);
  }
}

function handleEndDateInput(value) {
  tempEndDateInput.value = value;

  // Real-time validation for smart range selection
  if (isValidDateString(value)) {
    const parsedDate = parseInputDate(value);
    if (parsedDate) {
      const currentStartDate = tempValue.value.startDate;
      let newStartDate = currentStartDate;

      // Smart start date adjustment
      if (currentStartDate && parsedDate < currentStartDate) {
        newStartDate = parsedDate;
      }

      handleRangeSelect({
        startDate: newStartDate,
        endDate: parsedDate,
      });
    }
  }
}
</script>

<template>
  <CsPopover
    v-slot="{ isOpen }"
    :is-open="isPanelOpen"
    :is-manual="true"
    is-teleport
    @click-outside="handleClose"
  >
    <CsPopoverTrigger class="inline-flex">
      <slot
        :is-open="isOpen"
        :open="handleOpen"
        :close="handleClose"
        :date-display="dateDisplay"
      />
    </CsPopoverTrigger>

    <CsPopoverContent>
      <CsPopoverCard class="!shadow-brand-500">
        <CsPopoverCardContent class="min-w-[300px]">
          <div class="flex">
            <div
              v-if="isEnabledShortcuts && shortcuts.length > 0"
              class="max-h-[360px] overflow-hidden border-r border-brand-border"
            >
              <CsScrollShadow
                class="w-[210px] max-h-[360px] overflow-y-auto divide-y divide-brand-border"
              >
                <CsListbox v-for="group in shortcuts" :key="group.title">
                  <CsListboxItem
                    v-for="shortcut in group.shortcuts"
                    :key="shortcut.key"
                    :is-active="isShortcutActive(shortcut)"
                    :is-show-checkmark="isShortcutActive(shortcut)"
                    @click="handleShortcutClick(shortcut)"
                  >
                    {{ shortcut.label }}
                  </CsListboxItem>
                </CsListbox>
              </CsScrollShadow>
            </div>

            <div
              class="p-4"
              :class="[
                !isEnabledShortcuts || shortcuts.length === 0 ? 'border-0' : '',
              ]"
            >
              <div class="flex items-center gap-2 mb-2">
                <CsInput
                  ref="startDateInputRef"
                  size="sm"
                  :is-clearable="false"
                  :value="startDateDisplay"
                  :placeholder="
                    isStartDateFocused ? 'YYYY-MM-DD' : 'Start date'
                  "
                  class="w-full"
                  @focus="handleStartDateFocus"
                  @blur="handleStartDateBlur"
                  @input="handleStartDateInput"
                />

                <CsIcon name="arrow-right" class="flex-none" />

                <CsInput
                  ref="endDateInputRef"
                  size="sm"
                  :is-clearable="false"
                  :value="endDateDisplay"
                  :placeholder="isEndDateFocused ? 'YYYY-MM-DD' : 'End date'"
                  class="w-full"
                  @focus="handleEndDateFocus"
                  @blur="handleEndDateBlur"
                  @input="handleEndDateInput"
                />
              </div>

              <CsDatePickerPanel
                :start-date="tempValue.startDate"
                :end-date="tempValue.endDate"
                is-multimonth
                is-range
                :disable-dates-after="new Date()"
                @range-select="handleRangeSelect"
              />
            </div>
          </div>

          <div
            class="flex items-center gap-2 justify-end p-3 border-t border-brand-border"
          >
            <CsButton size="sm" variant="secondary" @click="handleCancel">
              Cancel
            </CsButton>

            <CsButton
              size="sm"
              variant="primary"
              :is-disabled="isApplyDisabled"
              @click="handleApply"
            >
              Apply
            </CsButton>
          </div>
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>
  </CsPopover>
</template>
